import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ElectionCategory } from '../../constants/categories';

export interface PlanItem {
  id: string;
  name: string;
  // Election post the plan is sold for; null only for plans made before categories existed
  category: ElectionCategory | null;
  // Rupees, the API sends it as a string like "1999.00"
  price: number | string;
  duration_days: number;
  description: string | null;
  features: string[];
  // null = unlimited
  max_wards: number | null;
  max_sub_ids: number | null;
  max_ai_posters: number | null;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;

  // Compatibility / UI helper fields
  isActive?: boolean;
  tagline?: string;
  code?: string;
  currency?: string;
  isPopular?: boolean;
  totalSubscribers?: number;
  priceMonthly?: number;
  priceAnnual?: number;
  maxVoters?: number;
  maxStaff?: number;
}

export type SubscriptionStatus = 'active' | 'trial' | 'expired' | 'cancelled';

export interface SubscriptionItem {
  id: string;
  subscriptionNumber: string;
  clientId: string;
  clientName: string;
  constituency: string;
  planId: string;
  planName: string;
  billingCycle: 'monthly' | 'annual';
  amount: number;
  currency: string;
  startDate: string;
  expiryDate: string;
  status: SubscriptionStatus;
  autoRenew: boolean;
  paymentMethod: string;
  lastPaymentDate: string;
  nextBillingDate: string;
  usageVoters: number;
  maxVoters: number;
  usageStaff: number;
  maxStaff: number;
  invoiceId?: string;
}

export interface PlanState {
  plans: PlanItem[];
  subscriptions: SubscriptionItem[];
  selectedPlan: PlanItem | null;
  loading: boolean;
  error: string | null;
}

export const normalizePlan = (item: any): PlanItem => {
  const is_active =
    item.is_active !== undefined
      ? Boolean(item.is_active)
      : item.isActive !== undefined
      ? Boolean(item.isActive)
      : true;

  const rawPrice = item.price !== undefined ? item.price : item.priceMonthly !== undefined ? item.priceMonthly : 0;
  const numPrice = typeof rawPrice === 'string' ? parseFloat(rawPrice) || 0 : Number(rawPrice) || 0;

  // null means unlimited, so it must not become 0
  const toLimit = (value: any) =>
    value === null || value === undefined || value === '' ? null : Number(value);

  return {
    id: String(item.id || ''),
    name: item.name || '',
    category: item.category || null,
    price: rawPrice,
    duration_days: Number(item.duration_days || 30),
    description: item.description || '',
    features: Array.isArray(item.features) ? item.features : [],
    max_wards: toLimit(item.max_wards),
    max_sub_ids: toLimit(item.max_sub_ids),
    max_ai_posters: toLimit(item.max_ai_posters),
    is_active,
    created_at: item.created_at || item.createdDate || new Date().toISOString(),
    updated_at: item.updated_at || new Date().toISOString(),

    // UI helpers
    isActive: is_active,
    tagline: item.tagline || item.description || '',
    code: item.code || (item.name ? item.name.toUpperCase().replace(/\s+/g, '_') : 'PLAN'),
    currency: item.currency || 'INR',
    isPopular: Boolean(item.isPopular),
    totalSubscribers: Number(item.totalSubscribers || 0),
    priceMonthly: numPrice,
    priceAnnual: numPrice * 10,
    maxVoters: item.maxVoters || Number(item.max_wards || 0) * 15000 || 50000,
    maxStaff: item.maxStaff || Number(item.max_sub_ids || 0) || 10,
  };
};

const initialPlans: PlanItem[] = [];

const initialState: PlanState = {
  plans: initialPlans,
  subscriptions: [],
  selectedPlan: null,
  loading: false,
  error: null,
};

const planSlice = createSlice({
  name: 'plans',
  initialState,
  reducers: {
    setPlanLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setPlanError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setPlans: (state, action: PayloadAction<PlanItem[]>) => {
      state.plans = (action.payload || []).map(normalizePlan);
    },
    addPlanReducer: (state, action: PayloadAction<PlanItem>) => {
      const normalized = normalizePlan(action.payload);
      const existingIdx = state.plans.findIndex((p) => p.id === normalized.id);
      if (existingIdx !== -1) {
        state.plans[existingIdx] = normalized;
      } else {
        state.plans.unshift(normalized);
      }
    },
    updatePlanReducer: (state, action: PayloadAction<PlanItem>) => {
      const normalized = normalizePlan(action.payload);
      const idx = state.plans.findIndex((p) => p.id === normalized.id);
      if (idx !== -1) {
        state.plans[idx] = normalized;
      } else {
        state.plans.unshift(normalized);
      }
      if (state.selectedPlan?.id === normalized.id) {
        state.selectedPlan = normalized;
      }
    },
    togglePlanStatusReducer: (
      state,
      action: PayloadAction<{ id: string; isActive?: boolean; is_active?: boolean }>
    ) => {
      const newStatus =
        action.payload.is_active !== undefined
          ? Boolean(action.payload.is_active)
          : Boolean(action.payload.isActive);

      const plan = state.plans.find((p) => p.id === action.payload.id);
      if (plan) {
        plan.is_active = newStatus;
        plan.isActive = newStatus;
        plan.updated_at = new Date().toISOString();
      }
      if (state.selectedPlan && state.selectedPlan.id === action.payload.id) {
        state.selectedPlan.is_active = newStatus;
        state.selectedPlan.isActive = newStatus;
        state.selectedPlan.updated_at = new Date().toISOString();
      }
    },
  },
});

export const {
  setPlanLoading,
  setPlanError,
  setPlans,
  addPlanReducer,
  updatePlanReducer,
  togglePlanStatusReducer,
} = planSlice.actions;

export default planSlice.reducer;
